/**
 * User API module for Platform Access
 * This demonstrates the API structure needed for the users table
 */

import { api, generateCRUDInterface } from "aldoo-ra/API"

/**
 * Escapes special regex characters in a string
 * @param {String} string - String to escape
 * @returns {String} Escaped string
 */
const escapeRegex = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Initialize CRUD API
const crudAPI = api("crud")
const userCRUD = generateCRUDInterface(crudAPI, "User")
const storeAccountCRUD = generateCRUDInterface(crudAPI, "StoreAccount")

/**
 * Extended User API methods for admin platform
 */
export const UserPlatformAPI = {
  
  /**
   * Get users with filtering and pagination
   * @param {Object} requestFields - MongoDB-style query filters
   * @returns {Promise<{result: boolean, data: Array, totalItems: number}>}
   */
  GetUsers: async (requestFields = {}) => {
    const defaultFields = {
      match: {
        deleted: { $ne: true }
      },
      sort: { created: -1 },
      itemsPerPage: 25,
      page: 1,
      ...requestFields
    }

    try {
      // Use the CRUD interface for getting users
      const response = await userCRUD.Get(defaultFields)
      
      // Ensure consistent response format
      return {
        result: true,
        data: response.data || [],
        totalItems: response.totalItems || 0
      }
    } catch (error) {
      console.error("Failed to fetch users:", error)
      return {
        result: false,
        data: [],
        totalItems: 0,
        error: error.message
      }
    }
  },

  /**
   * Get deleted users for restore functionality
   * @param {Object} requestFields - Additional query filters
   * @returns {Promise<{result: boolean, data: Array}>}
   */
  GetDeletedUsers: async (requestFields = {}) => {
    const fields = {
      ...requestFields,
      model: "User",
      match: { 
        ...requestFields.match,
        deleted: true
      },
      sort: { created: -1 },
    }

    return await crudAPI({ get: fields })
  },

  /**
   * Get full user details including devices and subscription info
   * @param {string} userId - User ID
   * @returns {Promise<{result: boolean, data: Object}>}
   */
  GetUserDetails: async (userId) => {
    try {
      const response = await userCRUD.Get({
        match: { _id: userId }
      })

      if (response.data && response.data.length > 0) {
        const user = response.data[0]
        
        // Optionally fetch additional data like subscriptions
        // const subscriptions = await getUserSubscriptions(userId)
        // user.subscriptions = subscriptions
        
        return {
          result: true,
          data: user
        }
      }

      return {
        result: false,
        error: "User not found"
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Update user information
   * @param {string} userId - User ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<{result: boolean, data?: Object}>}
   */
  UpdateUser: async (userId, updateData) => {
    try {
      const response = await userCRUD.Update({
        _id: userId,
        ...updateData
      })

      return {
        result: true,
        data: response.data
      }
    } catch (error) {
      console.error("Failed to update user:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Soft delete users
   * @param {Array<string>} userIds - Array of user IDs
   * @returns {Promise<{result: boolean}>}
   */
  DeleteUsers: async (userIds) => {
    try {
      // For bulk operations (>1 user), use special endpoint with history tracking
      if (userIds.length > 1) {
        const response = await crudAPI({
          bulkSoftDelete: {
            model: "User",
            ids: userIds,
            trackHistory: true
          }
        })

        return {
          result: response.result !== false,
          deletedCount: response.deletedCount || 0
        }
      }

      // For single user, use standard soft delete
      const promises = userIds.map(userId =>
        userCRUD.SoftDelete({ _id: userId })
      )

      const results = await Promise.all(promises)
      const successCount = results.filter(r => r.result).length

      return {
        result: successCount > 0,
        deletedCount: successCount
      }
    } catch (error) {
      console.error("Failed to delete users:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Hard delete users (permanent)
   * @param {Array<string>} userIds - Array of user IDs
   * @returns {Promise<{result: boolean}>}
   */
  HardDeleteUsers: async (userIds) => {
    try {
      // For bulk operations (>1 user), use special endpoint with history tracking
      if (userIds.length > 1) {
        const response = await crudAPI({
          bulkHardDelete: {
            model: "User",
            ids: userIds,
            trackHistory: true
          }
        })

        return {
          result: response.result !== false,
          deletedCount: response.deletedCount || 0
        }
      }

      // For single user, use standard delete
      const promises = userIds.map(userId =>
        userCRUD.HardDelete({ _id: userId })
      )

      const results = await Promise.all(promises)
      const successCount = results.filter(r => r.result).length

      return {
        result: successCount > 0,
        deletedCount: successCount
      }
    } catch (error) {
      console.error("Failed to hard delete users:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Restore deleted user
   * @param {string} userId - User ID
   * @returns {Promise<{result: boolean}>}
   */
  RestoreUser: async (userId) => {
    try {
      const update = {
        model: "User",
        data: { _id: userId, deleted: false }
      }
      return await crudAPI({ update })
    } catch (error) {
      console.error("Failed to restore user:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Restore deleted users (bulk)
   * @param {Array<string>} userIds - Array of user IDs
   * @returns {Promise<{result: boolean}>}
   */
  RestoreUsers: async (userIds) => {
    try {
      const promises = userIds.map(userId => UserPlatformAPI.RestoreUser(userId))
      const results = await Promise.all(promises)
      const successCount = results.filter(r => r.result !== false).length

      return {
        result: successCount > 0,
        restoredCount: successCount
      }
    } catch (error) {
      console.error("Failed to restore users:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Toggle user enabled/disabled status
   * @param {string} userId - User ID
   * @param {boolean} enabled - New enabled status
   * @returns {Promise<{result: boolean}>}
   */
  ToggleUserStatus: async (userId, enabled) => {
    try {
      const response = await userCRUD.Update({
        _id: userId,
        enabled
      })

      return {
        result: true,
        data: response.data
      }
    } catch (error) {
      console.error("Failed to toggle user status:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Search users by multiple fields
   * @param {string} searchQuery - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise<{result: boolean, data: Array}>}
   */
  SearchUsers: async (searchQuery, filters = {}) => {
    try {
      const searchFields = [
        'name',
        'email', 
        'google_id',
        'apple_id',
        'facebook_id',
        'internal_id',
        'stripe_customer_id'
      ]

      const searchConditions = searchFields.map(field => ({
        [field]: { $regex: escapeRegex(searchQuery), $options: 'i' }
      }))

      const requestFields = {
        match: {
          deleted: { $ne: true },
          $or: searchConditions,
          ...filters
        },
        sort: { created: -1 }
      }
      
      const response = await userCRUD.Get(requestFields)
      
      // Handle different response formats
      let userData = []
      if (Array.isArray(response)) {
        userData = response
      } else if (response.data && Array.isArray(response.data)) {
        userData = response.data
      } else if (response && Array.isArray(response.result)) {
        userData = response.result
      }
      
      return {
        result: true,
        data: userData
      }
    } catch (error) {
      console.error("Failed to search users:", error)
      return {
        result: false,
        data: [],
        error: error.message
      }
    }
  },

  /**
   * Get store account information for a user
   * @param {string} userId - User ID  
   * @param {string} userEmail - User email as fallback
   * @returns {Promise<{result: boolean, data: Object}>}
   */
  GetUserStoreAccount: async (userId, userEmail = null) => {
    try {
      // First try to find by user_id
      let response = await storeAccountCRUD.Get({
        match: { user_id: userId }
      })

      // If not found and email provided, try to find by email
      if ((!response.data || response.data.length === 0) && userEmail) {
        response = await storeAccountCRUD.Get({
          match: { email: userEmail }
        })
      }

      // If still not found, try with string conversion of userId
      if (!response.data || response.data.length === 0) {
        response = await storeAccountCRUD.Get({
          match: { user_id: userId.toString() }
        })
      }

      console.log("StoreAccount query result:", {
        userId,
        userEmail,
        found: response.data?.length || 0,
        data: response.data?.[0]
      })

      if (response.data && response.data.length > 0) {
        return {
          result: true,
          data: response.data[0]
        }
      }

      return {
        result: true,
        data: null
      }
    } catch (error) {
      console.error("Failed to fetch store account:", error)
      return {
        result: false,
        data: null,
        error: error.message
      }
    }
  },

  /**
   * Create a new user
   * @param {Object} userData - User data to create
   * @param {string} userData.name - User's full name
   * @param {string} userData.email - User's email address
   * @param {string} userData.status - User's status (active, inactive, suspended)
   * @returns {Promise<{result: boolean, data?: Object, error?: string}>}
   */
  CreateUser: async (userData) => {
    try {
      const response = await userCRUD.Create({
        name: userData.name,
        email: userData.email.toLowerCase().trim(),
        status: userData.status || 'active',
        created: new Date().toISOString(),
        enabled: userData.status === 'active'
      })

      return {
        result: true,
        data: response
      }
    } catch (error) {
      console.error("Failed to create user:", error)
      return {
        result: false,
        error: error.message
      }
    }
  },

  /**
   * Check if a user with the given email already exists
   * @param {string} email - Email to check
   * @returns {Promise<{result: boolean, exists: boolean, user?: Object}>}
   */
  CheckEmailExists: async (email) => {
    try {
      const response = await userCRUD.Get({
        match: {
          email: email.toLowerCase().trim(),
          deleted: { $ne: true }
        },
        limit: 1
      })

      // Handle different response formats
      let userData = []
      if (Array.isArray(response)) {
        userData = response
      } else if (response.data && Array.isArray(response.data)) {
        userData = response.data
      }

      const exists = userData.length > 0

      return {
        result: true,
        exists,
        user: exists ? userData[0] : null
      }
    } catch (error) {
      console.error("Failed to check email existence:", error)
      return {
        result: false,
        exists: false,
        error: error.message
      }
    }
  },

  /**
   * Get user statistics for overview dashboard
   * @returns {Promise<{result: boolean, data: Object}>}
   */
  GetUserStatistics: async () => {
    try {
      // Use the proper user API endpoint with correct request format
      const userAPI = api("user")
      
      const response = await userAPI({
        platform_users: {}
      })

      if (response.result) {
        return {
          result: true,
          data: response.data
        }
      } else {
        return {
          result: false,
          data: {
            total: 0,
            active: 0,
            disabled: 0,
            deleted: 0
          },
          error: response.error
        }
      }
    } catch (error) {
      console.error("Failed to fetch user statistics:", error)
      return {
        result: false,
        data: {
          total: 0,
          active: 0,
          disabled: 0,
          deleted: 0
        },
        error: error.message
      }
    }
  }
}

export default UserPlatformAPI